<template>
  <!-- The HTML comes from renderMarkdown(), which escapes its input before
       producing a single tag. See utils/markdown.js for why that is enough. -->
  <div class="markdown" v-html="html" />
</template>

<script setup>
import { computed } from 'vue'
import { renderMarkdown } from '../utils/markdown'

const props = defineProps({
  source: { type: String, default: '' },
  /**
   * Si un salto de linea suelto es un salto en pantalla.
   *
   * Por defecto si, que es lo que quiere una respuesta del asistente: el modelo
   * escribe una linea por idea y esos saltos son suyos.
   *
   * Un DOCUMENTO quiere lo contrario. La ayuda de un modulo se escribe a ochenta
   * columnas para que el fichero se lea en el repositorio, y con saltos duros
   * esas ochenta columnas pasaban a ser el ancho del texto en pantalla: el
   * renglon se cortaba a mitad de la columna y el resto quedaba en blanco.
   */
  hardBreaks: { type: Boolean, default: true },
  /**
   * `(href) => boolean`: qué enlaces se pueden seguir; el resto sale como texto.
   * Sin él se dibujan todos los de protocolo seguro (utils/markdown.js).
   */
  isTrusted: { type: Function, default: null },
})
const html = computed(() => renderMarkdown(props.source, { hardBreaks: props.hardBreaks, isTrusted: props.isTrusted }))
</script>

<style scoped>
.markdown { font-size: inherit; line-height: 1.65; }

.markdown :deep(p) { margin: 0 0 var(--space-3); }
.markdown :deep(p:last-child) { margin-bottom: 0; }

.markdown :deep(h3),
.markdown :deep(h4),
.markdown :deep(h5),
.markdown :deep(h6) {
  font-size: var(--text-md);
  font-weight: 650;
  margin: var(--space-4) 0 var(--space-2);
}
.markdown :deep(> :first-child) { margin-top: 0; }

.markdown :deep(ul),
.markdown :deep(ol) {
  margin: 0 0 var(--space-3);
  padding-left: var(--space-5);
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
}
.markdown :deep(li) { line-height: 1.55; }

.markdown :deep(code) {
  font-family: var(--font-mono);
  font-size: 0.88em;
  padding: 1px 5px;
  border-radius: 4px;
  background: var(--color-gray-100);
}

.markdown :deep(pre) {
  margin: 0 0 var(--space-3);
  padding: var(--space-3);
  border-radius: var(--radius-sm);
  background: var(--color-gray-100);
  overflow-x: auto;
}
.markdown :deep(pre code) { padding: 0; background: none; }

.markdown :deep(blockquote) {
  margin: 0 0 var(--space-3);
  padding-left: var(--space-3);
  border-left: 2px solid var(--color-border);
  color: var(--color-text-soft);
}

.markdown :deep(hr) {
  border: none;
  border-top: 1px solid var(--color-border);
  margin: var(--space-4) 0;
}

/* ── Los esquemas ────────────────────────────────────────────────────────────
   Lo que dibuja `utils/diagram.js`: cajas y líneas, nunca una imagen. El texto
   de dentro es el mismo del `.md`, así que se traduce, se busca, se selecciona
   y se lee en voz alta — y se ve nítido a cualquier tamaño.

   Las flechas y las líneas del árbol son pseudo-elementos: no se copian con el
   texto, no las lee un lector de pantalla (la lista ya dice que hay orden) y no
   se descuadran cuando una traducción alarga una palabra. */
.markdown :deep(.dg) {
  margin: var(--space-4) 0;
  /* Contra el `padding-left` que llevan `ul` y `ol` ahi arriba: en un esquema
     esa sangria descuadra la linea que une una rama con su padre. */
  padding: 0;
  list-style: none;
}

/* Pasos: en fila cuando cabe, en columna cuando no. La flecha cambia de
   dirección con ellos, que es la razón de que no sea un carácter del texto. */
.markdown :deep(.dg--steps) {
  display: flex;
  /* Explicito: la regla de `ol` de mas arriba pone `column` para las listas
     normales, y sin esto los pasos salian apilados uno bajo otro —cada caja a
     todo el ancho— en vez de en fila. Se ve midiendo, no leyendo. */
  flex-direction: row;
  flex-wrap: wrap;
  gap: var(--space-2);
  align-items: stretch;
}
.markdown :deep(.dg-step) {
  position: relative;
  flex: 1 1 8rem;
  min-width: 7rem;
  padding: var(--space-3);
  border: 1px solid var(--color-border);
  border-radius: var(--radius);
  background: var(--color-surface);
}
.markdown :deep(.dg-step + .dg-step) { margin-left: var(--space-4); }
.markdown :deep(.dg-step + .dg-step)::before {
  content: '→';
  position: absolute;
  left: calc(var(--space-4) * -1);
  top: 50%;
  width: var(--space-4);
  transform: translateY(-50%);
  text-align: center;
  color: var(--color-text-mute);
}
.markdown :deep(.dg-step__label) {
  display: block;
  font-weight: 650;
  font-size: var(--text-sm);
  line-height: 1.3;
}
.markdown :deep(.dg-step__note) {
  display: block;
  margin-top: var(--space-1);
  color: var(--color-text-soft);
  font-size: var(--text-xs);
  line-height: 1.45;
}

/* Niveles: una escalera. Lo que se entiende de un vistazo es que cada peldaño
   es MÁS que el anterior, y eso lo dice el ancho de la barra, no un número. */
.markdown :deep(.dg--levels) {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}
.markdown :deep(.dg-level) {
  position: relative;
  padding: var(--space-2) var(--space-3);
  border-radius: var(--radius-sm);
  background: var(--color-surface-2);
  overflow: hidden;
}
.markdown :deep(.dg-level__bar) {
  position: absolute;
  inset: 0 auto 0 0;
  background: var(--color-primary-soft);
  border-right: 2px solid var(--color-primary);
}
.markdown :deep(.dg-level__label),
.markdown :deep(.dg-level__note) { position: relative; }
.markdown :deep(.dg-level__label) { font-weight: 650; font-size: var(--text-sm); }
.markdown :deep(.dg-level__note) {
  display: block;
  color: var(--color-text-soft);
  font-size: var(--text-xs);
  line-height: 1.45;
}

/* Árbol: la sangría la pone el CSS y la línea que une una rama con su padre es
   un borde. Sin caracteres de dibujo, que es lo que se descuadra al traducir. */
.markdown :deep(.dg--tree) {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: var(--space-3);
  border: 1px solid var(--color-border);
  border-radius: var(--radius);
  background: var(--color-surface);
  font-size: var(--text-sm);
}
.markdown :deep(.dg-node) {
  position: relative;
  padding: var(--space-1) var(--space-2);
  line-height: 1.4;
}
.markdown :deep(.dg-node--1) { margin-left: var(--space-5); }
.markdown :deep(.dg-node--2) { margin-left: calc(var(--space-5) * 2); }
.markdown :deep(.dg-node--3) { margin-left: calc(var(--space-5) * 3); }
.markdown :deep(.dg-node--4) { margin-left: calc(var(--space-5) * 4); }
.markdown :deep(.dg-node:not(.dg-node--0))::before {
  content: '';
  position: absolute;
  left: calc(var(--space-3) * -1);
  top: 0;
  bottom: 50%;
  width: var(--space-3);
  border-left: 1px solid var(--color-border);
  border-bottom: 1px solid var(--color-border);
  border-bottom-left-radius: 6px;
}
.markdown :deep(.dg-node__label) { font-weight: 550; }
.markdown :deep(.dg-node__note) {
  margin-left: var(--space-2);
  color: var(--color-text-soft);
  font-size: var(--text-xs);
}

@media (max-width: 40rem) {
  /* Sin sitio para la fila: los pasos bajan, y la flecha con ellos. */
  .markdown :deep(.dg--steps) { flex-direction: column; }
  .markdown :deep(.dg-step + .dg-step) { margin-left: 0; margin-top: var(--space-4); }
  .markdown :deep(.dg-step + .dg-step)::before {
    content: '↓';
    left: 50%;
    top: calc(var(--space-4) * -1);
    transform: translateX(-50%);
    width: auto;
  }
}

.markdown :deep(a) { text-decoration: underline; }
</style>
